import { useEffect, useRef, useState } from 'react'
import { IconoBuscar } from '../../componentes/iconos'
import type { OpcionFiltro } from '../../hooks/useFiltrosCatalogo'
import type { Filtros } from '../../hooks/useFiltrosUrl'

/** Sin espera, cada tecla seria una peticion y una entrada de historial. */
const ESPERA_BUSQUEDA = 300

interface Props {
  filtros: Filtros
  semanas: OpcionFiltro[]
  rutas: OpcionFiltro[]
  onCambiar: (clave: 'semana' | 'ruta' | 'q', valor: string | undefined) => void
}

const CLASE_CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo'

export function BarraFiltros({ filtros, semanas, rutas, onCambiar }: Props) {
  const [texto, setTexto] = useState(filtros.q)
  // Ultimo valor que este componente ha puesto en la url. Sirve para distinguir
  // "lo he escrito yo" de "la url ha cambiado por fuera" (atras del navegador).
  const ultimoEnviado = useRef(filtros.q)

  useEffect(() => {
    if (filtros.q === ultimoEnviado.current) return
    ultimoEnviado.current = filtros.q
    setTexto(filtros.q)
  }, [filtros.q])

  useEffect(() => {
    if (texto === ultimoEnviado.current) return
    const temporizador = setTimeout(() => {
      ultimoEnviado.current = texto
      onCambiar('q', texto || undefined)
    }, ESPERA_BUSQUEDA)
    return () => clearTimeout(temporizador)
  }, [texto, onCambiar])

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <label htmlFor="buscador" className="block text-sm font-medium text-tinta-2">
          Buscar cliente
        </label>
        <div className="relative mt-1">
          <IconoBuscar
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
          />
          <input
            id="buscador"
            type="search"
            autoComplete="off"
            placeholder="Nombre, móvil o población"
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            className={`${CLASE_CAMPO} pl-9`}
          />
        </div>
      </div>

      <div>
        <label htmlFor="semana" className="block text-sm font-medium text-tinta-2">
          Semana
        </label>
        <select
          id="semana"
          value={filtros.semana ?? ''}
          onChange={(evento) => onCambiar('semana', evento.target.value || undefined)}
          className={`${CLASE_CAMPO} mt-1`}
        >
          {/* Las opciones salen de /api/filtros: el backend es la fuente de
              verdad y una lista escrita a mano se desincronizaria con el. */}
          <option value="">Todas las semanas</option>
          {semanas.map((opcion) => (
            <option key={opcion.clave} value={opcion.clave}>
              {opcion.etiqueta}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="ruta" className="block text-sm font-medium text-tinta-2">
          Ruta
        </label>
        <select
          id="ruta"
          value={filtros.ruta ?? ''}
          onChange={(evento) => onCambiar('ruta', evento.target.value || undefined)}
          className={`${CLASE_CAMPO} mt-1`}
        >
          <option value="">Todas las rutas</option>
          {rutas.map((opcion) => (
            <option key={opcion.clave} value={opcion.clave}>
              {opcion.etiqueta}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
